"use client";

import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceBenefitItem,
  ServiceInfoBox,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import Link from "next/link";

// Service image from public directory
const sdsImage = "/images/services/svc-software-development.jpg";

// Breaks a band section out of the layout's centered container to full viewport width.
const FULL_BLEED = "mx-[calc(50%_-_50vw)]";

const sdsFAQs = [
  {
    question: "How much does custom software development cost in Malaysia?",
    answer: "Custom software development in Malaysia typically ranges from RM 20,000 for simple web applications to RM 300,000+ for complex enterprise systems. Anchor Sprint provides detailed quotations based on your requirements, with phased development options to manage budget. Our Selangor-based team offers competitive rates compared to KL agencies."
  },
  {
    question: "How long does it take to develop a custom software application?",
    answer: "Timeline depends on complexity. A basic web application takes 6-10 weeks, a mobile app 8-14 weeks, and a full enterprise system 4-8 months. Anchor Sprint uses agile development with 2-week sprints, so you see working software within the first month and can provide feedback throughout the process."
  },
  {
    question: "What technologies does Anchor Sprint use for software development?",
    answer: "We use modern tech stacks including React, Next.js, and Vue.js for frontend; Node.js, Python, and .NET for backend; React Native and Flutter for mobile; and AWS, Azure, and GCP for cloud infrastructure. We select the best technology for your specific project requirements and long-term maintainability."
  },
  {
    question: "Do you provide ongoing maintenance and support after delivery?",
    answer: "Yes, Anchor Sprint provides comprehensive post-launch support including bug fixes, security updates, performance monitoring, feature enhancements, and 24/7 technical support. We offer flexible maintenance plans from basic support to fully managed services, ensuring your software stays reliable and up-to-date."
  },
  {
    question: "Can Anchor Sprint modernize or upgrade our existing legacy software?",
    answer: "Absolutely. We specialize in legacy system modernization including code refactoring, database migration, cloud migration, API integration, and complete rebuilds using modern frameworks. We assess your current system, create a migration roadmap, and execute incrementally to minimize business disruption."
  }
];

// Technology stack rendered inside the dark terminal proof moment.
const techStackGroups = [
  {
    label: "Front-end Technologies",
    items: [
      "React / React Native",
      "Angular",
      "Vue.js",
      "Flutter",
      "Modern HTML5/CSS3/JavaScript",
    ],
  },
  {
    label: "Back-end Technologies",
    items: [
      "Node.js",
      "Python (Django, Flask)",
      "Java (Spring)",
      "PHP (Laravel, Symfony)",
      ".NET Core",
    ],
  },
  {
    label: "Database & Cloud Infrastructure",
    items: [
      "SQL: PostgreSQL, MySQL, Microsoft SQL Server",
      "NoSQL: MongoDB, Redis, Elasticsearch",
      "Cloud: AWS, Azure, Google Cloud Platform",
      "DevOps: Docker, Kubernetes, CI/CD pipelines",
    ],
  },
];

const supportServices = [
  {
    title: "24/7 Monitoring:",
    description: "Continuous oversight of application performance and availability",
  },
  {
    title: "Preventive Maintenance:",
    description: "Regular updates and optimizations to prevent issues before they occur",
  },
  {
    title: "Technical Support:",
    description: "Dedicated support team available through multiple channels",
  },
  {
    title: "Feature Enhancements:",
    description: "Continuous improvement based on user feedback and emerging needs",
  },
  {
    title: "Security Updates:",
    description: "Proactive security patches and vulnerability management",
  },
];

export default function SoftwareDevelopment() {
  const locale = useLocale();

  return (
    <ServicePageLayout
      title="Software Development and Support in Malaysia"
      breadcrumb="Services / Software Development"
      currentServiceId="sds"
      dek="At Anchor Sprint, we deliver comprehensive software development services that transform your business requirements into powerful, scalable, and user-friendly software solutions. Our experienced team combines technical expertise with industry knowledge to create custom applications that drive efficiency, innovation, and growth for your organization."
    >
      <ServiceHeroImage
        src={sdsImage}
        alt="Software Development"
      />

      <ServiceSection
        title="Our Software Development Expertise"
        eyebrow="What we build"
        accent="Software Development"
        dek="Custom applications, mobile apps, and modernized platforms — engineered around your workflows."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceGrid columns={2}>
          <ServiceFeatureCard
            icon="fas fa-laptop-code"
            title="Custom Software Development Applications"
            description="Tailored software solutions designed specifically for your unique business requirements and workflows, ensuring maximum relevance and impact."
          />

          <ServiceFeatureCard
            icon="fas fa-mobile-alt"
            title="Custom Mobile App Development"
            description="Native and cross-platform mobile applications that deliver exceptional user experiences across iOS and Android devices."
          />

          <ServiceFeatureCard
            icon="fas fa-globe"
            title="Web Applications"
            description="Responsive, feature-rich web applications built with modern frameworks and technologies for optimal performance and scalability."
          />

          <ServiceFeatureCard
            icon="fas fa-sync"
            title="Legacy Modernization"
            description="Transform outdated systems into modern, efficient applications while preserving valuable business logic and data."
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="Technical Approach"
        eyebrow="How we work"
        accent="Approach"
        dek="The stack and the method behind every build — chosen for performance, maintainability, and fit."
        band="surface"
        className={FULL_BLEED}
      >
        {/* Dark proof moment — the technology stack as a terminal panel */}
        <div className="mb-8">
          <h3 className="mb-4 font-display text-xl font-bold text-neutral-emphasis">
            {t(locale, "Our Technology Stack")}
          </h3>
          <div className="overflow-hidden rounded-3xl bg-hero-background">
            <div className="flex items-center gap-2 border-b border-primary-contrast/[0.08] px-6 py-4">
              <span className="h-2.5 w-2.5 rounded-full bg-primary-contrast/20" aria-hidden />
              <span className="h-2.5 w-2.5 rounded-full bg-primary-contrast/20" aria-hidden />
              <span className="h-2.5 w-2.5 rounded-full bg-primary-contrast/20" aria-hidden />
              <span className="ml-3 font-mono text-xs text-primary-contrast/50">
                ~/anchor-sprint/tech-stack
              </span>
            </div>
            <div className="grid grid-cols-1 gap-8 p-6 sm:p-8 md:grid-cols-3 lg:p-10">
              {techStackGroups.map((group) => (
                <div key={group.label}>
                  <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary-contrast/40">
                    {t(locale, group.label)}
                  </p>
                  <ul className="space-y-2.5">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 font-mono text-sm leading-relaxed text-primary-contrast/75"
                      >
                        <span
                          className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary"
                          aria-hidden
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <ServiceInfoBox
          title="Development Methodology"
          description="We follow industry-best agile development practices to ensure transparency, flexibility, and consistent delivery of value:"
          variant="blue"
        >
          <ul className="list-disc pl-5 text-neutral-muted space-y-2">
            <li><span className="font-medium">Discovery & Planning:</span> Thorough requirements analysis and detailed project roadmap development</li>
            <li><span className="font-medium">Iterative Development:</span> Regular sprints with continuous integration and delivery</li>
            <li><span className="font-medium">Quality Assurance:</span> Comprehensive testing strategy including automated and manual testing</li>
            <li><span className="font-medium">Deployment:</span> Smooth deployment with minimal disruption to business operations</li>
            <li><span className="font-medium">Post-Launch Support:</span> Ongoing maintenance, monitoring, and continuous improvement</li>
          </ul>
        </ServiceInfoBox>

        <p className="mt-6 text-neutral-muted">
          {t(locale, "We can add")}{" "}
          <Link
            href={getLocalizedPathname("/enterprise/sonarqube-malaysia/", locale)}
            className="font-medium text-primary underline hover:text-primary-emphasis"
          >
            {t(locale, "SonarQube code quality checks")}
          </Link>{" "}
          {t(locale, "to your build, so issues are caught before they reach your users.")}
        </p>
      </ServiceSection>

      <ServiceSection
        title="Business Benefits"
        eyebrow="Why it matters"
        accent="Benefits"
        dek="What custom software changes for the business — beyond the code."
        band="ivory"
        className={FULL_BLEED}
      >
        <div className="grid grid-cols-1 gap-6">
          <ServiceBenefitItem
            title="Increased Operational Efficiency"
            description="Custom software automates repetitive tasks, streamlines workflows, and optimizes resource allocation, leading to substantial operational improvements."
          />

          <ServiceBenefitItem
            title="Enhanced Customer Experience"
            description="User-centric design and development create intuitive interfaces and seamless customer journeys, improving satisfaction and loyalty."
          />

          <ServiceBenefitItem
            title="Scalable Growth"
            description="Forward-thinking architecture ensures your software can evolve with your business, accommodating increased users, transactions, and new features."
          />

          <ServiceBenefitItem
            title="Competitive Advantage"
            description="Proprietary software tailored to your unique processes gives you a distinct edge in the marketplace that off-the-shelf solutions cannot match."
          />
        </div>
      </ServiceSection>

      <ServiceSection
        title="Our Support Services"
        eyebrow="Beyond launch"
        accent="Support"
        dek="Launch is the starting line — we keep your software fast, secure, and improving."
        band="surface"
        className={FULL_BLEED}
      >
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
          <ul className="space-y-4">
            {supportServices.map((service) => (
              <li key={service.title} className="flex items-start gap-3">
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                  aria-hidden
                />
                <span className="text-neutral-muted">
                  <span className="font-medium text-neutral-emphasis">
                    {t(locale, service.title)}
                  </span>{" "}
                  {t(locale, service.description)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Cross-link to the remedial service: buyers arriving here often already
            have something built and breaking, not a greenfield project. */}
        <p className="mt-6 text-neutral-muted">
          {t(
            locale,
            "Already have something built — quickly, often with an AI coding assistant — that works in the demo and breaks in production?",
          )}{" "}
          <Link
            href={getLocalizedPathname("/services/vibe-code-rescue/", locale)}
            className="font-medium text-primary underline hover:text-blue-800"
          >
            {t(locale, "Vibe Code Rescue")}
          </Link>{" "}
          {t(
            locale,
            "audits what you have first, then fixes it against criteria agreed in writing before the work starts.",
          )}
        </p>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        eyebrow="FAQ"
        accent="Questions"
        dek="Common questions about custom software development in Malaysia."
        band="ivory"
        className={FULL_BLEED}
      >
        <ServiceFaq items={sdsFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={sdsFAQs} />

      <ConsultationCTA
        title="Ready to build software that moves your business?"
        description="Let's discuss how our software development expertise can help you achieve your business objectives and create lasting competitive advantages."
        buttonText="Request a consultation"
      />
    </ServicePageLayout>
  );
}
